import {ADAPTER_QUERY_KEY_PREFIX} from '../../api/constant';
import type {
  SeriesCardViewLandingListQueryVariables,
  SeriesListViewLandingListQueryVariables,
  SeriesPosterViewLandingListQueryVariables,
  SetListViewLandingListQueryVariables,
  SetPosterViewLandingListQueryVariables,
  SingleListViewLandingListQueryVariables,
  StaticLandingDayOfWeekLayoutQueryVariables,
  StaticLandingDayOfWeekSectionQueryVariables,
  StaticLandingEventLayoutQueryVariables,
  StaticLandingEventSectionQueryVariables,
  StaticLandingFreeSeriesLayoutQueryVariables,
  StaticLandingFreeSeriesSectionQueryVariables,
  StaticLandingGenreLayoutQueryVariables,
  StaticLandingGenreSectionQueryVariables,
  StaticLandingOriginalLayoutQueryVariables,
  StaticLandingOriginalSectionQueryVariables,
  StaticLandingRankingLayoutQueryVariables,
  StaticLandingRankingSectionQueryVariables,
  StaticLandingRankingThemeLayoutQueryVariables,
  StaticLandingRankingThemeSectionQueryVariables,
  StaticLandingTodayNewLayoutQueryVariables,
  StaticLandingTodayNewSectionQueryVariables,
  StaticLandingTodayUpLayoutQueryVariables,
  StaticLandingTodayUpSectionQueryVariables,
} from '../../graphql/generated/types';

/**
 * Landing 도메인 Query Key 관리
 *
 * 기존 GraphQL generated types의 getKey 로직을 그대로 재사용
 */
export const LANDING_QUERY_KEY_MAP = {
  /**
   * 시리즈 카드뷰 랜딩 리스트 (Infinite)
   * Origin: useInfiniteSeriesCardViewLandingListQuery.getKey
   */
  seriesCardViewLandingList: (
    variables: SeriesCardViewLandingListQueryVariables
  ) =>
    [
      ADAPTER_QUERY_KEY_PREFIX,
      'seriesCardViewLandingList.infinite',
      variables,
    ] as const,

  /**
   * 시리즈 리스트뷰 랜딩 리스트 (Infinite)
   * Origin: useInfiniteSeriesListViewLandingListQuery.getKey
   */
  seriesListViewLandingList: (
    variables: SeriesListViewLandingListQueryVariables
  ) =>
    [
      ADAPTER_QUERY_KEY_PREFIX,
      'seriesListViewLandingList.infinite',
      variables,
    ] as const,

  /**
   * 시리즈 포스터뷰 랜딩 리스트 (Infinite)
   * Origin: useInfiniteSeriesPosterViewLandingListQuery.getKey
   */
  seriesPosterViewLandingList: (
    variables: SeriesPosterViewLandingListQueryVariables
  ) =>
    [
      ADAPTER_QUERY_KEY_PREFIX,
      'seriesPosterViewLandingList.infinite',
      variables,
    ] as const,

  /**
   * 세트 리스트뷰 랜딩 리스트 (Infinite)
   * Origin: useInfiniteSetListViewLandingListQuery.getKey
   */
  setListViewLandingList: (variables: SetListViewLandingListQueryVariables) =>
    [
      ADAPTER_QUERY_KEY_PREFIX,
      'setListViewLandingList.infinite',
      variables,
    ] as const,

  /**
   * 세트 포스터뷰 랜딩 리스트 (Infinite)
   * Origin: useInfiniteSetPosterViewLandingListQuery.getKey
   */
  setPosterViewLandingList: (
    variables: SetPosterViewLandingListQueryVariables
  ) =>
    [
      ADAPTER_QUERY_KEY_PREFIX,
      'setPosterViewLandingList.infinite',
      variables,
    ] as const,

  /**
   * 단편 리스트뷰 랜딩 리스트 (Infinite)
   * Origin: useInfiniteSingleListViewLandingListQuery.getKey
   */
  singleListViewLandingList: (
    variables: SingleListViewLandingListQueryVariables
  ) =>
    [
      ADAPTER_QUERY_KEY_PREFIX,
      'singleListViewLandingList.infinite',
      variables,
    ] as const,

  /**
   * 요일연재 고정랜딩 섹션 (Infinite)
   * Origin: useInfiniteStaticLandingDayOfWeekSectionQuery.getKey
   */
  staticLandingDayOfWeekSection: (
    variables: StaticLandingDayOfWeekSectionQueryVariables
  ) =>
    [
      ADAPTER_QUERY_KEY_PREFIX,
      'staticLandingDayOfWeekSection.infinite',
      variables,
    ] as const,

  /**
   * 이벤트 고정랜딩 섹션 (Infinite)
   * Origin: useInfiniteStaticLandingEventSectionQuery.getKey
   */
  staticLandingEventSection: (
    variables: StaticLandingEventSectionQueryVariables
  ) =>
    [
      ADAPTER_QUERY_KEY_PREFIX,
      'staticLandingEventSection.infinite',
      variables,
    ] as const,
  /**
   * 무료 시리즈 고정 랜딩 섹션 (Infinite)
   * Origin: useInfiniteStaticLandingFreeSeriesSectionQuery.getKey
   */
  infiniteStaticLandingFreeSeriesSection: (
    variables: StaticLandingFreeSeriesSectionQueryVariables
  ) => [
    ADAPTER_QUERY_KEY_PREFIX,
    'staticLandingFreeSeriesSection.infinite',
    variables,
  ],

  /**
   * 장르 고정 랜딩 섹션 (Infinite)
   * Origin: useInfiniteStaticLandingGenreSectionQuery.getKey
   */
  infiniteStaticLandingGenreSection: (
    variables: StaticLandingGenreSectionQueryVariables
  ) => [
    ADAPTER_QUERY_KEY_PREFIX,
    'staticLandingGenreSection.infinite',
    variables,
  ],

  /**
   * 오리지널 고정 랜딩 섹션 (Infinite)
   * Origin: useInfiniteStaticLandingOriginalSectionQuery.getKey
   */
  infiniteStaticLandingOriginalSection: (
    variables: StaticLandingOriginalSectionQueryVariables
  ) => [
    ADAPTER_QUERY_KEY_PREFIX,
    'staticLandingOriginalSection.infinite',
    variables,
  ],

  /**
   * 랭킹 고정 랜딩 섹션 (Infinite)
   * Origin: useInfiniteStaticLandingRankingSectionQuery.getKey
   */
  infiniteStaticLandingRankingSection: (
    variables: StaticLandingRankingSectionQueryVariables
  ) => [
    ADAPTER_QUERY_KEY_PREFIX,
    'staticLandingRankingSection.infinite',
    variables,
  ],

  /**
   * 단행본 랭킹 테마 고정 랜딩 섹션 (Infinite)
   * Origin: useInfiniteStaticLandingRankingThemeSectionQuery.getKey
   */
  infiniteStaticLandingRankingThemeSection: (
    variables: StaticLandingRankingThemeSectionQueryVariables
  ) => [
    ADAPTER_QUERY_KEY_PREFIX,
    'staticLandingRankingThemeSection.infinite',
    variables,
  ],

  /**
   * 단행본 랭킹 테마 고정랜딩 레이아웃
   * Origin: useStaticLandingRankingThemeLayoutQuery.getKey
   */
  staticLandingRankingThemeLayout: (
    variables?: StaticLandingRankingThemeLayoutQueryVariables
  ) => [ADAPTER_QUERY_KEY_PREFIX, 'staticLandingRankingThemeLayout', variables],

  /**
   * 오늘UP 고정 랜딩 섹션 (Infinite)
   * Origin: useInfiniteStaticLandingTodayUpSectionQuery.getKey
   */
  infiniteStaticLandingTodayUpSection: (
    variables: StaticLandingTodayUpSectionQueryVariables
  ) => [
    ADAPTER_QUERY_KEY_PREFIX,
    'staticLandingTodayUpSection.infinite',
    variables,
  ],

  /**
   * 오늘NEW 고정 랜딩 섹션 (Infinite)
   * Origin: useInfiniteStaticLandingTodayNewSectionQuery.getKey
   */
  infiniteStaticLandingTodayNewSection: (
    variables: StaticLandingTodayNewSectionQueryVariables
  ) => [
    ADAPTER_QUERY_KEY_PREFIX,
    'staticLandingTodayNewSection.infinite',
    variables,
  ],

  /**
   * 요일연재 고정랜딩 레이아웃
   * Origin: useStaticLandingDayOfWeekLayoutQuery.getKey
   */
  staticLandingDayOfWeekLayout: (
    variables?: StaticLandingDayOfWeekLayoutQueryVariables
  ) => [ADAPTER_QUERY_KEY_PREFIX, 'staticLandingDayOfWeekLayout', variables],

  /**
   * 이벤트 고정랜딩 레이아웃
   * Origin: useStaticLandingEventLayoutQuery.getKey
   */
  staticLandingEventLayout: (
    variables?: StaticLandingEventLayoutQueryVariables
  ) => [ADAPTER_QUERY_KEY_PREFIX, 'staticLandingEventLayout', variables],

  /**
   * 무료시리즈 고정랜딩 레이아웃
   * Origin: useStaticLandingFreeSeriesLayoutQuery.getKey
   */
  staticLandingFreeSeriesLayout: (
    variables?: StaticLandingFreeSeriesLayoutQueryVariables
  ) => [ADAPTER_QUERY_KEY_PREFIX, 'staticLandingFreeSeriesLayout', variables],

  /**
   * 장르 고정랜딩 레이아웃
   * Origin: useStaticLandingGenreLayoutQuery.getKey
   */
  staticLandingGenreLayout: (
    variables?: StaticLandingGenreLayoutQueryVariables
  ) => [ADAPTER_QUERY_KEY_PREFIX, 'staticLandingGenreLayout', variables],

  /**
   * 오리지널 고정랜딩 레이아웃
   * Origin: useStaticLandingOriginalLayoutQuery.getKey
   */
  staticLandingOriginalLayout: (
    variables?: StaticLandingOriginalLayoutQueryVariables
  ) => [ADAPTER_QUERY_KEY_PREFIX, 'staticLandingOriginalLayout', variables],

  /**
   * 랭킹 고정랜딩 레이아웃
   * Origin: useStaticLandingRankingLayoutQuery.getKey
   */
  staticLandingRankingLayout: (
    variables?: StaticLandingRankingLayoutQueryVariables
  ) => [ADAPTER_QUERY_KEY_PREFIX, 'staticLandingRankingLayout', variables],

  /**
   * 오늘NEW 고정랜딩 레이아웃
   * Origin: useStaticLandingTodayNewLayoutQuery.getKey
   */
  staticLandingTodayNewLayout: (
    variables?: StaticLandingTodayNewLayoutQueryVariables
  ) => [ADAPTER_QUERY_KEY_PREFIX, 'staticLandingTodayNewLayout', variables],

  /**
   * 오늘UP 고정랜딩 레이아웃
   * Origin: useStaticLandingTodayUpLayoutQuery.getKey
   */
  staticLandingTodayUpLayout: (
    variables?: StaticLandingTodayUpLayoutQueryVariables
  ) => [ADAPTER_QUERY_KEY_PREFIX, 'staticLandingTodayUpLayout', variables],
} as const;

export const LANDING_MUTATION_KEY_MAP = {} as const;
